import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import qs from 'qs';

import { getFormValues, submit, isSubmitting, reset } from 'redux-form';

import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';

import Progress from 'components/common/Progress';
import Dialog, {
  DialogActions,
  DialogContent,
  DialogTitle,
} from 'components/common/Dialog';
import Button from 'components/common/Button';

import { fetchOrder } from 'redux/modules/orders';
import { fetchArchiveOrder } from 'redux/modules/archiveOrders';
import { fetchTemplates } from 'redux/modules/templates';
import { fetchBusiness } from 'redux/modules/business';
import { fetchSystemSettings } from 'redux/modules/systemSettings';

import throttle from 'utils/throttle';
import formatOrderNumber from 'utils/formatOrderNumber';

import PrintDialogForm from './PrintDialogForm';

const styles = () => ({
  action: {
    '&:first-child': {
      marginRight: 'auto',
    },
  },
});

class PrintDialog extends Component {
  constructor(props) {
    super(props);

    this.state = {
      limit: 10,
      offset: 0,
      triggerLoading: true,
    };

    this.onDialogEnter = this.onDialogEnter.bind(this);
    this.onDialogExited = this.onDialogExited.bind(this);
    this.onTemplatesScrollFetchRequest = this.onTemplatesScrollFetchRequest.bind(this);

    this.onTemplatesRequest = throttle(this.onTemplatesRequest.bind(this));
  }

  componentWillReceiveProps(nextProps) {
    const { open, templatesCount, loading } = this.props;
    const newState = {};

    if (templatesCount === 0 || !open) {
      newState.triggerLoading = true;
    } else {
      newState.triggerLoading = false;
    }
    if (open && nextProps.loading < loading) {
      newState.triggerLoading = false;
    }
    this.setState(newState);
  }

  componentDidUpdate(prevProps, prevState) {
    const { open } = this.props;
    const { limit, offset } = this.state;
    const limitChanged = prevState.limit !== limit;
    const offsetChanged = prevState.offset !== offset;
    if (open && (limitChanged || offsetChanged)) {
      this.onTemplatesRequest();
    }
  }

  onTemplatesRequest() {
    const { actions } = this.props;
    const { limit, offset, triggerLoading } = this.state;
    actions.fetchTemplates({
      limit,
      offset,
      triggerLoading,
      push: offset > 0,
    });
  }

  onDialogEnter() {
    const { diagnosis, ID, actions, variant } = this.props;
    this.onTemplatesRequest();
    this.props.actions.fetchBusiness({
      triggerLoading: true,
    });
    this.props.actions.fetchSystemSettings({
      triggerLoading: true,
    });
    if (variant === 'order' && ID && !diagnosis) {
      if (this.props['Is Archive']) {
        actions.fetchArchiveOrder({
          ID,
          triggerLoading: true,
        });
      } else {
        actions.fetchOrder({
          ID,
          triggerLoading: true,
        });
      }
    }
  }

  onDialogExited() {
    this.setState({
      offset: 0,
      triggerLoading: true,
    });
  }

  onTemplatesScrollFetchRequest() {
    const { offset, limit } = this.state;
    if ((this.props.templates.length - limit) >= offset) {
      this.setState({ offset: offset + limit });
    }
  }

  render() {
    const {
      open,
      onClose,
      onSign,
      loading,
      templates,
      classes,
      printFormValues,
      actions,
      patient,
      order,
      business,
      systemSettings,
      account,
      form,
      submitting,
      ID,
      variant,
      ...otherProps
    } = this.props;
    const buttonPrintAllDisabled = !Object.keys(printFormValues)
      .find(key => printFormValues[key] === true);
    const editPreviewItems = Object.keys(printFormValues)
      .reduce((accumulator, currentValue) => {
        if (printFormValues[currentValue] === true) {
          if (currentValue.indexOf('Document') !== -1) {
            const documentID = Number(currentValue.split('_')[1]);
            const orderLineIDs = currentValue.split('_')[2];
            return {
              ...accumulator,
              ...orderLineIDs
                ? { [`document-${documentID}`]: [
                  ...accumulator[`document-${documentID}`] || [],
                  orderLineIDs,
                ] }
                : { [`document-${documentID}`]: null },
            };
          }
          if (currentValue.indexOf('Template') !== -1) {
            const templateID = Number(currentValue.split('_')[1]);
            return {
              ...accumulator,
              ...{ [`template-${templateID}`]: null },
            };
          }
          return accumulator;
        }
        return accumulator;
      }, {});
    const buttonEditPreviewDisabled = Object.keys(editPreviewItems).length === 0;
    const editPreviewHrefParams = qs.stringify(editPreviewItems, {
      encode: false, addQueryPrefix: true,
    });
    let editPreviewHrefPrefix = '';
    let editPreviewHrefStem = '';
    if (variant === 'patient') {
      editPreviewHrefPrefix = `/charts/${patient['Record Number']}/print`;
    }
    if (variant === 'order') {
      if (otherProps['Is Archive']) {
        editPreviewHrefPrefix = `/workflow/archive_order/print/${ID}`;
      } else {
        editPreviewHrefPrefix = `/workflow/order/print/${ID}`;
      }
    }
    if (Object.keys(editPreviewItems).join().indexOf('document') !== -1) {
      const firstDocumentValue = Object.keys(editPreviewItems)
        .find(item => item.indexOf('document') !== -1);
      const firstDocumentID = firstDocumentValue.replace('document-', '');
      const orderLine = editPreviewItems[firstDocumentValue];
      editPreviewHrefStem = orderLine
        ? `/document/${firstDocumentID}/${orderLine[0]}`
        : `/document/${firstDocumentID}`;
    } else if (Object.keys(editPreviewItems).join().indexOf('template') !== -1) {
      editPreviewHrefStem =
        `/template/${Object.keys(editPreviewItems).find(item => item.indexOf('template') !== -1).replace('template-', '')}`;
    }
    const editPreviewHref = `${editPreviewHrefPrefix}${editPreviewHrefStem}${editPreviewHrefParams}`;
    return (
      <Dialog
        open={open}
        onClose={onClose}
        onEnter={this.onDialogEnter}
        onExited={this.onDialogExited}
        maxWidth="md"
      >
        <DialogTitle onCloseClick={onClose}>
          <Typography
            variant="title"
            color="secondary"
            align="center"
          >
            {variant === 'patient' &&
              `Print Data For ${patient['Last Name']}, ${patient['First Name']}`
            }
            {variant === 'order' &&
              `Print Data For Order Number ${formatOrderNumber(ID)}`
            }
          </Typography>
        </DialogTitle>
        <DialogContent
          withScrollbars
          disableGutters
          fullHeight
          onScrollFrame={(event) => {
            if (event.top > 0.8) {
              this.onTemplatesScrollFetchRequest();
            }
          }}
        >
          {(loading > 0) ? (
            <Progress />
          ) : (
            <PrintDialogForm
              form={form}
              templates={templates}
              patient={patient}
              order={order}
              business={business}
              systemSettings={systemSettings}
              formValues={printFormValues}
              account={account}
              {...otherProps}
            />
          )}
        </DialogContent>
        <DialogActions
          classes={{
            action: classes.action,
          }}
        >
          <Button
            color="primary"
            underline
            disabled={buttonEditPreviewDisabled}
            href={editPreviewHref}
            target="_blank"
            onClick={() => {
              const documentEditPreviewItems = Object.keys(editPreviewItems)
                .filter(item => item.indexOf('document') !== -1)
                .map(item => Number(item.replace('document-', '')));
              const templateEditPreviewItems = Object.keys(editPreviewItems)
                .filter(item => item.indexOf('template') !== -1)
                .map(item => Number(item.replace('template-', '')));
              if (variant === 'patient') {
                documentEditPreviewItems.forEach(item => {
                  actions.reset(`${patient['Record Number']}-patient-${item}-documentPreviewFill`);
                });
                templateEditPreviewItems.forEach(item => {
                  actions.reset(`${patient['Record Number']}-patient-${item}-templatePreviewFill`);
                });
              } else if (variant === 'order') {
                documentEditPreviewItems.forEach(item => {
                  actions.reset(`${ID}-order-${item}-documentPreviewFill`);
                });
                templateEditPreviewItems.forEach(item => {
                  actions.reset(`${ID}-order-${item}-templatePreviewFill`);
                });
              }
            }}
          >
            Edit / Preview
          </Button>
          <Button
            disabled={buttonPrintAllDisabled || submitting}
            onClick={() => onSign(printFormValues)}
            variant="raised"
          >
            Sign
          </Button>
          <Button
            variant="raised"
            color="primary"
            disabled={buttonPrintAllDisabled || submitting}
            onClick={() => { actions.submit(form); }}
          >
            Print all
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}

PrintDialog.propTypes = {
  form: PropTypes.string.isRequired,
  templates: PropTypes.arrayOf(PropTypes.shape({
    ID: PropTypes.number.isRequired,
  })).isRequired,
  templatesCount: PropTypes.number.isRequired,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onSign: PropTypes.func,
  loading: PropTypes.number.isRequired,
  submitting: PropTypes.bool.isRequired,
  actions: PropTypes.shape({
    fetchTemplates: PropTypes.func.isRequired,
    fetchBusiness: PropTypes.func.isRequired,
    fetchSystemSettings: PropTypes.func.isRequired,
    submit: PropTypes.func.isRequired,
  }).isRequired,
  classes: PropTypes.shape({
    action: PropTypes.string.isRequired,
  }).isRequired,
  patient: PropTypes.shape({
    'First Name': PropTypes.string.isRequired,
    'Last Name': PropTypes.string.isRequired,
    'Record Number': PropTypes.number.isRequired,
  }).isRequired,
  business: PropTypes.shape({
    ID: PropTypes.number,
  }),
  // eslint-disable-next-line react/forbid-prop-types
  systemSettings: PropTypes.object.isRequired,
  ID: PropTypes.number,
  variant: PropTypes.oneOf(['patient', 'order']),
  printFormValues: PropTypes.shape({
    Data: PropTypes.object,
    Documents: PropTypes.object,
    Templates: PropTypes.object,
  }),
  appInfo: PropTypes.shape({
    'APP NAME': PropTypes.string,
  }).isRequired,
};

PrintDialog.defaultProps = {
  open: false,
  onClose: () => {},
  onSign: () => {},
  printFormValues: {},
  business: {},
  variant: 'patient',
  ID: null,
};

const mapStateToProps = (store, ownProps) => {
  const printFormValues = getFormValues(ownProps.form)(store);
  return {
    templates: store.templates.data,
    templatesCount: store.templates.count,
    loading: store.templates.loading
      + store.business.loading
      + store.systemSettings.loading
      + store.orders.loading,
    printFormValues,
    business: store.business.data,
    systemSettings: store.systemSettings.data,
    submitting: isSubmitting(ownProps.form)(store),
    appInfo: store.appInfo,
    account: store.account,
  };
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({
    fetchTemplates,
    fetchBusiness,
    fetchSystemSettings,
    fetchOrder,
    fetchArchiveOrder,
    submit,
    reset,
  }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(
  withStyles(styles)(PrintDialog),
);



// WEBPACK FOOTER //
// ./src/components/common/PrintDialog/PrintDialog.js